import { Destroyer } from 'app/common/utils';
import { ChartsCommonService } from 'app/charts-dashboard/services/charts-common.service';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Injector,
  Input,
  OnDestroy
} from '@angular/core';
import {
  ChartFilterPatternNameEnum,
  ChartTypeEnum,
  CustomControlsNameEnum,
  IChartFilter,
  ParameterId,
  RealTimeEnum,
  SystemControlsNameEnum
} from 'app/charts-dashboard/interfaces/chart.interface';
import {
  IChangeIVDiffStockSeries,
  IChangeStockSeries,
  ICompareDateDialog,
  ICompareSymbol,
  SymbolDialogActionEnum
} from 'app/charts-dashboard/interfaces';
import {
  dateFormat,
  dropdownTemplateField
} from '../../../helpers/charts-module-helpers';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { getAllInstruments } from '@iv/store/instruments/instrument.reducer';
import { ICard } from 'app/charts-dashboard/state/card';
import {
  ChartsDashboardSeriesQuery,
  IChartSeries,
  SeriesTypeEnum
} from 'app/charts-dashboard/state/series';
import { ID } from '@datorama/akita';
import { ChartsDialogsService } from 'app/charts-dashboard/services/charts-dialogs.service';
import { ChartsDashboardStateService } from 'app/charts-dashboard/state/charts-dashboard-state.service';

@Component({
  selector: 'iv-live-card-chart-actions',
  template: '<ng-container></ng-container>',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardChartActionsComponent extends Destroyer implements OnDestroy {
  constructor(
    protected readonly chartsCommonService: ChartsCommonService,
    @Inject(AppStore) protected store: Redux.Store<AppState>,
    @Inject(Injector) protected readonly injector: Injector,
    protected readonly chartsDialogsService: ChartsDialogsService,
    protected readonly chartsState: ChartsDashboardStateService,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery
  ) {
    super();
  }

  @Input()
  public card: ICard<ID>;

  public chartNameId = ChartTypeEnum;
  public filterTypes = {...ChartFilterPatternNameEnum, ...CustomControlsNameEnum, ...SystemControlsNameEnum};
  public realtimeToggle = RealTimeEnum;

  public dateFormat = dateFormat;
  public isFullScreen: boolean;
  public maxCountSeries = 10;

  public get compareButtonFunc() {
    return this.card.chartId !== this.chartNameId.TERM_STRUCTURE_ROLLING && this.card.chartId !== this.chartNameId.SKEW_CHART
      ? this.compareStock(this.card.id)
      : this.compareDate(this.card.id);
  }

  public get mainSeries(): IChartSeries {
    return this.card.chartSeries.map((id: ID) => this.seriesQuery.getEntity(id)).find((series: IChartSeries) => series.type === SeriesTypeEnum.MAIN);
  }

  public get isHistorical(): boolean {
    return this.chartsCommonService.isHistorical(this.card.chartId);
  }

  public get isMaturity(): boolean {
    return this.chartsCommonService.isMaturity(this.card.chartId);
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public contentWidth(controlName): boolean {
    return controlName !== this.filterTypes.TIME_RANGE &&
      controlName !== this.filterTypes.EOD_TIME_RANGE &&
      controlName !== this.filterTypes.MONEYNESS_FIRST &&
      controlName !== this.filterTypes.MONEYNESS_SECOND &&
      controlName !== this.filterTypes.TOTAL_OI_CHART_TYPE &&
      controlName !== this.filterTypes.SINGLE_CONTRACT_ANALYZER_TYPE;
  }

  public dropdownTemplateField(controlName: string): { list: string, input: string } {
    return dropdownTemplateField(controlName);
  }

  public changeSeries(parentCardId: string, seriesId: ID): void {
    const action = () => {
      if (this.card.chartId === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER) return SymbolDialogActionEnum.CHANGE_STOCK_IN_INTRADAY_CHART;
      return this.isHistorical ? SymbolDialogActionEnum.CHANGE_STOCK_IN_MAIN_SERIES : SymbolDialogActionEnum.CHANGE_STOCK_IN_ALL_SERIES;
    };

    const data: IChangeStockSeries = {
      action: action(),
      instruments: getAllInstruments(this.store.getState()),
      filters: this.card.filters,
      selectedFilters: this.mainSeries.selectedFilters,
      seriesId,
      parentCardId
    };

    this.chartsDialogsService.createCompareSymbolDialog(this.injector, data);
  }

  public changeIVDiffStockSeries(parentCardId: string, seriesId: ID, currentPoint: 'first' | 'second'): void {
    const data: IChangeIVDiffStockSeries = {
      action: SymbolDialogActionEnum.CHANGE_IV_DIFF_STOCK_IN_SERIES,
      instruments: getAllInstruments(this.store.getState()),
      filters: this.card.filters,
      selectedFilters: this.seriesQuery.getEntity(seriesId).selectedFilters,
      seriesId,
      parentCardId,
      currentPoint
    };

    this.chartsDialogsService.createCompareSymbolDialog(this.injector, data);
  }

  public compareStock(parentCardId: ID): void {
    const data: ICompareSymbol = {
      action: SymbolDialogActionEnum.COMPARE_SERIES,
      filters: this.card.filters,
      selectedFilters: this.mainSeries.selectedFilters,
      instruments: getAllInstruments(this.store.getState()),
      parentCardId
    };

    this.chartsDialogsService.createCompareSymbolDialog(this.injector, data);
  }

  public compareDate(parentCardId: ID): void {
    const data: ICompareDateDialog = {
      action: SymbolDialogActionEnum.COMPARE_DATE,
      filters: this.card.filters,
      selectedFilters: this.mainSeries.selectedFilters,
      seriesId: null,
      parentCardId
    };

    this.chartsDialogsService.createCompareDateDialog(this.injector, data);
  }

  public getChartFilter(filterKey: ParameterId): IChartFilter {
    return this.card.filters.find((filter: IChartFilter) => filter.parameterId === filterKey);
  }

  public getFilterLabel(filterKey: ParameterId): string | null {
    if (
      filterKey === ChartFilterPatternNameEnum.MONEYNESS ||
      ((this.card.chartId === ChartTypeEnum.IMPLIED_VOLATILITY || this.card.chartId === ChartTypeEnum.SKEW_CHART) &&
        filterKey === ChartFilterPatternNameEnum.IVX_TERM)
    ) {
      return this.getChartFilter(filterKey).name;
    } else return null;
  }
}
